import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, Loader2, AlertCircle, Car, Train, ExternalLink } from "lucide-react";
import { SectionHeading } from "../site/SectionHeading";
import { ConsentMap } from "../site/ConsentMap";
import { SITE, EMERGENCY, ACCESS_INFO, PHARMACY_PHOTOS, photoSrcSet } from "@/lib/site-data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                subject: form.subject,
                message: form.message,
            });
            toast.success("Merci, votre message a bien été envoyé.", {
                description: "Nous revenons vers vous dans les meilleurs délais.",
            });
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Une erreur est survenue.", {
                description: typeof detail === "string" ? detail : "Merci de réessayer dans quelques instants.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" className="py-24 lg:py-32 bg-white" data-testid="contact-section">
            <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
                <SectionHeading
                    eyebrow="Contact & accès"
                    title={<>Poussez la porte, <span className="italic text-brand-primary">ou écrivez-nous</span>.</>}
                    subtitle="Une question, un conseil, une demande particulière — nous sommes à votre écoute. Vous pouvez aussi prendre rendez-vous directement en ligne."
                />

                <div className="mt-12 grid lg:grid-cols-12 gap-8 lg:gap-10">
                    {/* Left column — infos */}
                    <div className="lg:col-span-5 space-y-6">
                        {/* Coordonnées */}
                        <div className="bg-white border border-brand-line p-8">
                            <p className="ll-eyebrow text-brand-primary mb-5">Nous trouver</p>
                            <div className="space-y-5 font-sans">
                                <Row icon={<MapPin className="h-4 w-4" />} label="Adresse">
                                    <p className="text-brand-ink">
                                        {SITE.legalName}<br />
                                        {SITE.address.line1}<br />
                                        {SITE.address.zip} {SITE.address.city}
                                    </p>
                                    <a
                                        href={SITE.mapsLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-block mt-1 text-sm text-brand-primary ll-link-underline"
                                        data-testid="contact-maps-link"
                                    >
                                        Itinéraire
                                    </a>
                                </Row>
                                <Row icon={<Phone className="h-4 w-4" />} label="Téléphone">
                                    <a href={`tel:${SITE.phone}`} className="text-brand-ink hover:text-brand-primary">
                                        {SITE.phoneDisplay}
                                    </a>
                                </Row>
                                <Row icon={<Mail className="h-4 w-4" />} label="Courriel">
                                    <a href={`mailto:${SITE.email}`} className="text-brand-ink hover:text-brand-primary">
                                        {SITE.email}
                                    </a>
                                </Row>
                            </div>
                        </div>

                        {/* Horaires */}
                        <div className="bg-white border border-brand-line p-8">
                            <div className="flex items-center gap-3 mb-5">
                                <Clock className="h-4 w-4 text-brand-primary" />
                                <p className="ll-eyebrow text-brand-primary">Horaires d'ouverture</p>
                            </div>
                            <ul className="font-sans text-sm divide-y divide-brand-line">
                                {SITE.hours.map((h) => (
                                    <li key={h.day} className="flex justify-between py-2.5">
                                        <span className="text-brand-ink/85">{h.day}</span>
                                        <span className="text-brand-ink/60">{h.time}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Google Maps + exterior photo */}
                        <div className="overflow-hidden border border-brand-line" data-testid="contact-exterior-photo">
                            <img
                                src={PHARMACY_PHOTOS.exterior}
                                srcSet={photoSrcSet(PHARMACY_PHOTOS.exterior)}
                                sizes="(min-width: 1024px) 560px, 100vw"
                                alt="Extérieur de la Pharmacie La Lutrive"
                                loading="lazy"
                                className="w-full aspect-[16/10] object-cover"
                            />
                        </div>
                        <div className="aspect-[16/10] overflow-hidden border border-brand-line" data-testid="contact-map">
                            <ConsentMap src={SITE.mapsEmbed} title="Carte de Lutry" directionsHref={SITE.mapsLink} />
                        </div>

                        {/* Accès — parkings & transports */}
                        <div className="bg-white border border-brand-line p-8" data-testid="access-info">
                            <p className="ll-eyebrow text-brand-primary mb-5">Comment venir</p>
                            <div className="space-y-5 font-sans text-sm">
                                <div className="flex items-start gap-3">
                                    <div className="h-9 w-9 rounded-full bg-brand-mint/40 text-brand-primary inline-flex items-center justify-center shrink-0">
                                        <Car className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <p className="text-brand-ink font-medium mb-1.5">Parkings à proximité</p>
                                        <p className="text-brand-ink/65 mb-1" data-testid="parking-short-stay">{ACCESS_INFO.parkingNote}</p>
                                        <ul className="space-y-1 text-brand-ink/65">
                                            {ACCESS_INFO.parking.map((p) => <li key={p}>• {p}</li>)}
                                        </ul>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="h-9 w-9 rounded-full bg-brand-mint/40 text-brand-primary inline-flex items-center justify-center shrink-0">
                                        <Train className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <p className="text-brand-ink font-medium mb-1.5">Transports publics</p>
                                        <ul className="space-y-1 text-brand-ink/65">
                                            {ACCESS_INFO.transport.map((p) => <li key={p}>• {p}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right column — form + emergency */}
                    <div className="lg:col-span-7 space-y-6">
                        {/* Form */}
                        <form
                            onSubmit={submit}
                            className="bg-brand-cream p-8 lg:p-10 border border-brand-line"
                            data-testid="contact-form"
                        >
                            <p className="ll-eyebrow text-brand-primary mb-6">Écrivez-nous</p>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field id="name" label="Nom complet" value={form.name} onChange={update("name")} required testId="contact-name-input" />
                                <Field id="email" type="email" label="Courriel" value={form.email} onChange={update("email")} required testId="contact-email-input" />
                                <Field id="phone" label="Téléphone (optionnel)" value={form.phone} onChange={update("phone")} testId="contact-phone-input" />
                                <Field id="subject" label="Sujet" value={form.subject} onChange={update("subject")} required testId="contact-subject-input" />
                            </div>
                            <div className="mt-5">
                                <label htmlFor="message" className="ll-eyebrow text-brand-ink/60 block mb-2">Votre message</label>
                                <textarea
                                    id="message"
                                    value={form.message}
                                    onChange={update("message")}
                                    required
                                    rows={5}
                                    className="w-full bg-transparent border-b border-brand-ink/30 focus:border-brand-primary outline-none py-3 font-sans text-brand-ink resize-none transition-colors"
                                    data-testid="contact-message-input"
                                />
                            </div>
                            <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="text-xs text-brand-ink/55 font-sans max-w-sm" data-testid="contact-privacy-notice">
                                    Vos informations sont utilisées uniquement pour répondre à votre demande et supprimées après 12 mois.{" "}
                                    <Link to="/confidentialite" className="underline underline-offset-2 text-brand-primary">Politique de confidentialité</Link>
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="ll-btn ll-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                    data-testid="contact-submit-btn"
                                >
                                    {submitting ? (
                                        <><Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours…</>
                                    ) : (
                                        <>Envoyer le message <Send className="h-4 w-4" /></>
                                    )}
                                </button>
                            </div>
                        </form>

                        {/* Off-hours / emergency */}
                        <div className="bg-brand-ink text-brand-cream p-8 lg:p-10" data-testid="off-hours-block">
                            <div className="flex items-start gap-3 mb-6">
                                <AlertCircle className="h-5 w-5 text-brand-mint mt-0.5 shrink-0" />
                                <div>
                                    <p className="ll-eyebrow text-brand-mint mb-1">En dehors des heures d'ouverture</p>
                                    <h3 className="font-display text-2xl leading-tight">Service de garde — Vaud</h3>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-6">
                                {/* Pharmacie 24 card */}
                                <a
                                    href={`tel:${EMERGENCY.pharmacie24.phone}`}
                                    className="group bg-white text-brand-ink p-6 hover:bg-brand-cream transition-colors flex flex-col"
                                    data-testid="pharmacie24-card"
                                >
                                    <div className="h-24 flex items-center justify-center mb-4 -mx-2">
                                        <img
                                            src={EMERGENCY.pharmacie24.logoSrc}
                                            alt={EMERGENCY.pharmacie24.name}
                                            className="max-h-24 w-auto object-contain"
                                        />
                                    </div>
                                    <p className="font-sans text-sm text-brand-ink/65 text-center">
                                        {EMERGENCY.pharmacie24.address}
                                    </p>
                                    <div className="mt-4 pt-4 border-t border-brand-line text-center">
                                        <p className="font-display text-lg text-brand-primary font-medium">
                                            {EMERGENCY.pharmacie24.phoneDisplay}
                                        </p>
                                        <p className="font-sans text-xs text-brand-ink/55 mt-1">
                                            {EMERGENCY.pharmacie24.hours}
                                        </p>
                                    </div>
                                </a>

                                {/* Other emergency contacts */}
                                <div className="bg-brand-primary/15 p-5 flex flex-col justify-between">
                                    <div>
                                        <a
                                            href={EMERGENCY.gardeLink.href}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-start gap-2 text-brand-cream hover:text-brand-mint group"
                                            data-testid="garde-link"
                                        >
                                            <ExternalLink className="h-4 w-4 mt-1 shrink-0" />
                                            <span className="font-sans text-sm leading-snug underline underline-offset-4 decoration-brand-mint/40">
                                                {EMERGENCY.gardeLink.label}
                                            </span>
                                        </a>
                                    </div>
                                    <div className="mt-6 pt-6 border-t border-brand-cream/20">
                                        <p className="ll-eyebrow text-brand-mint mb-2">
                                            {EMERGENCY.centraleMedecins.label}
                                        </p>
                                        <a
                                            href={`tel:${EMERGENCY.centraleMedecins.phone}`}
                                            className="font-display text-2xl text-brand-cream hover:text-brand-mint inline-flex items-center gap-2"
                                            data-testid="centrale-phone"
                                        >
                                            <Phone className="h-5 w-5" />
                                            {EMERGENCY.centraleMedecins.phoneDisplay}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Row = ({ icon, label, children }) => (
    <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-full bg-brand-mint/40 flex items-center justify-center text-brand-primary shrink-0">
            {icon}
        </div>
        <div>
            <p className="text-xs text-brand-ink/50 uppercase tracking-wider">{label}</p>
            {children}
        </div>
    </div>
);

const Field = ({ id, label, value, onChange, type = "text", required = false, testId }) => (
    <div>
        <label htmlFor={id} className="ll-eyebrow text-brand-ink/60 block mb-2">
            {label}{required && <span className="text-brand-primary"> *</span>}
        </label>
        <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full bg-transparent border-b border-brand-ink/30 focus:border-brand-primary outline-none py-2.5 font-sans text-brand-ink transition-colors"
            data-testid={testId}
        />
    </div>
);

export default Contact;
